import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import Reticle from './Reticle';

// Styled Components
const Container = styled.div`
	position: relative;
	width: 100%;
	height: 100%;

	img {
		width: 100%;
		height: 100%;
		display: block;
		object-fit: ${(props) => (props.$isExpanded ? 'cover' : 'contain')};
		margin: 0;
	}

	.clip-svg {
		clip-path: url(#${(props) => props.$clipId});
	}
`;

// Generate unique ID for clip path
const generateUniqueId = () => `clip-${Math.random().toString(36).substr(2, 9)}`;

export const InlineClippedImage = ({ src, alt, clipPaths = [], transitionDuration = 0.8, transitionDelay = 0, transitionEase = 'easeOut', dimensions = { width: 0, height: 0 }, onLoad, threshold = 0.5, debug = false }) => {
	const [clipId] = useState(generateUniqueId());
	const [imageLoaded, setImageLoaded] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [calculatedDimensions, setCalculatedDimensions] = useState({
		width: dimensions.width,
		height: dimensions.height,
		left: 0,
		top: 0,
	});

	const containerRef = useRef(null);
	const imageRef = useRef(null);

	// Handle expansion based on scroll position
	useEffect(() => {
		if (debug) {
			setIsExpanded(false);
			return;
		}

		const checkInitialState = () => {
			if (containerRef.current) {
				const rect = containerRef.current.getBoundingClientRect();
				const isVisible = rect.top < window.innerHeight;
				if (isVisible) setIsExpanded(true);
			}
		};

		if (document.readyState === 'complete') {
			requestAnimationFrame(checkInitialState);
		} else {
			window.addEventListener('load', () => requestAnimationFrame(checkInitialState));
			return () => window.removeEventListener('load', checkInitialState);
		}
	}, [debug]);

	// Intersection Observer setup
	useEffect(() => {
		if (isExpanded || debug) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setTimeout(() => {
						setIsExpanded(true);
						observer.unobserve(entry.target);
					}, 300);
				}
			},
			{ threshold }
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => observer.disconnect();
	}, [threshold, isExpanded, debug]);

	// Handle image dimensions
	useEffect(() => {
		const updateDimensions = () => {
			if (containerRef.current) {
				const container = containerRef.current.getBoundingClientRect();
				const imgRatio = dimensions.width / dimensions.height;

				if (container.width !== calculatedDimensions.width) {
					setCalculatedDimensions({
						width: container.width,
						height: container.width / imgRatio,
						left: 0,
						top: 0,
					});
				}
			}
		};

		updateDimensions();

		const debouncedUpdate = debounce(updateDimensions, 100);
		window.addEventListener('resize', debouncedUpdate);
		return () => window.removeEventListener('resize', debouncedUpdate);
	}, [dimensions, calculatedDimensions.width]);

	// Utility functions
	const calculateValue = (value, baseDimension) => (typeof value === 'string' && value.endsWith('%') ? (parseFloat(value) / 100) * baseDimension : value);

	const getPathValues = (path) => {
		const imageRect = imageRef.current?.getBoundingClientRect();
		const containerRect = containerRef.current?.getBoundingClientRect();

		if (!imageRect || !containerRect) {
			return { x: 0, y: 0, width: 0, height: 0 };
		}

		// Calculate image position relative to container
		const left = imageRect.left - containerRect.left;
		const top = imageRect.top - containerRect.top;

		return {
			x: calculateValue(path.x, imageRect.width) + left,
			y: calculateValue(path.y, imageRect.height) + top,
			width: path.width ? calculateValue(path.width, imageRect.width) : calculateValue(path.height, imageRect.height),
			height: path.height ? calculateValue(path.height, imageRect.height) : calculateValue(path.width, imageRect.width),
		};
	};

	const handleImageLoad = (e) => {
		const rect = imageRef.current?.getBoundingClientRect();
		if (rect) {
			setCalculatedDimensions({
				width: rect.width,
				height: rect.height,
				left: 0,
				top: 0,
			});
		}
		setImageLoaded(true);
		if (onLoad) onLoad(e);
	};

	return (
		<Container
			className="inline-clipped-image"
			ref={containerRef}
			$clipId={clipId}
			$isExpanded={isExpanded}
			style={{
				opacity: imageLoaded ? 1 : 0,
				transition: 'opacity 0.3s ease-in-out',
			}}
		>
			<img ref={imageRef} src={src} alt={alt} onLoad={handleImageLoad} className={`clip-svg ${debug ? 'debug-image' : ''}`} />
			{<Reticle active={true} />}
			<svg
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
					pointerEvents: 'none',
				}}
				preserveAspectRatio="none"
			>
				<defs>
					<clipPath id={clipId}>
						{imageLoaded &&
							clipPaths.map((path, index) => {
								const initial = getPathValues(path.initial);
								const expanded = getPathValues(path.expanded);

								return (
									<motion.rect
										key={index}
										style={{ position: 'absolute' }}
										initial={initial}
										animate={isExpanded ? expanded : initial}
										transition={{
											duration: transitionDuration,
											delay: transitionDelay,
											ease: transitionEase,
										}}
									/>
								);
							})}
					</clipPath>
				</defs>
			</svg>
		</Container>
	);
};

// Utility function to debounce resize events
const debounce = (fn, ms) => {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => fn.apply(this, args), ms);
	};
};
